// src/components/features/timetables/DetailsPage/TimetableDetailsPage.tsx
import { useEffect, useState } from 'preact/hooks';
import { StateMessage } from '../../../base/StateMessage/StateMessage';
import { DetailsPage } from '../../../widget/Pages/Pages';
import { Tab, Tabs } from '../../../base/Tabs/Tabs';
import { ViewportOverlay } from '../../../base/ViewportOverlay/ViewportOverlay';
import { Timetable } from '../Timetable/Timetable';
import type { TimetableGridRow } from '../Grid/TimetableGrid';
import { TimetableHeader } from '../Header/TimetableHeader';
import { TimetableMap } from '../TimetableMap/TimetableMap';
import { DisruptionAlert } from '../../../base/DisruptionAlert/DisruptionAlert';
import { useTimetableNotesEnabled } from '../../../../config/TenantContext';
// Types
import type { TimetableDetailsLine } from '../../../../data/timetableDetailsLine';
import type { TimetableTable } from '../../../../data/useTimetable';
import type { LineAssets } from '../../../../arcticapi/types';
import type { ComponentProps } from 'preact';
import { normalizeDirection } from '../href';
// Styles
import styles from './TimetableDetailsPage.module.css';

function getDirectionLabels(
  selectedLine: TimetableDetailsLine | null,
  timetable: TimetableTable | null,
): Record<'outbound' | 'inbound', string> {
  const labels: Record<'outbound' | 'inbound', string> = {
    outbound: 'Outbound',
    inbound: 'Inbound',
  };

  for (const direction of selectedLine?.directions ?? []) {
    const normalizedDirection = normalizeDirection(direction.name);

    if (normalizedDirection && direction.destination) {
      labels[normalizedDirection] = `To ${direction.destination}`;
    }
  }

  const currentDirection = normalizeDirection(
    timetable?.direction?.name ?? null,
  );

  if (currentDirection && timetable?.direction?.destination) {
    labels[currentDirection] = `To ${timetable.direction.destination}`;
  }

  return labels;
}

interface TimetableDetailsPageProps {
  selectedLine: TimetableDetailsLine | null;
  activeDetailsTab: 'timetable' | 'map';
  onTabChange: (nextTabId: 'timetable' | 'map') => void;
  timetableLoading: boolean;
  timetableError: Error | null;
  unavailableMessage: string | null;
  timetable: TimetableTable | null;
  lineAssets: LineAssets | null;
  selectedDirection: 'outbound' | 'inbound';
  stopDisplay: 'major' | 'all';
  controlsStopDisplay?: 'major' | 'all';
  date: string;
  onDirectionChange: (value: 'outbound' | 'inbound') => void;
  onStopDisplayChange: (value: 'major' | 'all') => void;
  onDateChange: (value: string) => void;
  onSubmit: (event: SubmitEvent) => void;
  onShowStopDeparturesDetails?: (waypoint: TimetableGridRow) => void;
}

/**
 * Contains the Timetable (controls and grid) and map tabs.
 */
export function TimetableDetailsPage({
  selectedLine,
  activeDetailsTab,
  onTabChange,
  timetableLoading,
  timetableError,
  unavailableMessage,
  timetable,
  lineAssets,
  selectedDirection,
  stopDisplay,
  controlsStopDisplay,
  date,
  onDirectionChange,
  onStopDisplayChange,
  onDateChange,
  onSubmit,
  onShowStopDeparturesDetails,
}: TimetableDetailsPageProps) {
  const [expanded, setExpanded] = useState(false);
  const timetableNotesEnabled = useTimetableNotesEnabled();
  const shouldShowTimetable =
    timetable !== null || timetableLoading || timetableError !== null;
  const directionLabels = getDirectionLabels(selectedLine, timetable);

  // The line colors are applied as CSS variables to the tabs container,
  // which allows the active tab to use them for its background and text color.
  const lineThemeStyle = {
    '--timetable-line-background':
      selectedLine?.colors?.background ?? 'var(--widget-brand-secondary)',
    '--timetable-line-foreground':
      selectedLine?.colors?.foreground ?? '#FFFFFF',
  } as ComponentProps<'div'>['style'];

  useEffect(() => {
    if (!selectedLine && expanded) {
      setExpanded(false);
    }
  }, [expanded, selectedLine]);

  if (!selectedLine) {
    return (
      <DetailsPage>
        <StateMessage
          state="error"
          title="Timetable details unavailable"
          message="We couldn't load this timetable. Go back to results and try another service."
        />
      </DetailsPage>
    );
  }

  return (
    <DetailsPage>
      <ViewportOverlay
        open={expanded}
        onOpenChange={setExpanded}
        className={styles.detailsOverlay}
      >
        <div className={styles.timetableDetailsPageWrapper}>
          <TimetableHeader
            line={selectedLine}
            expanded={expanded}
            onToggleExpand={() =>
              setExpanded((currentExpanded) => !currentExpanded)
            }
          />
          <DisruptionAlert disruption={selectedLine.disruption} />
          <div
            data-testid="timetable-details-tabs"
            className={styles.detailsTabsHost}
            style={lineThemeStyle}
          >
            <Tabs
              activeTab={activeDetailsTab}
              onTabChange={(nextTabId) => {
                if (nextTabId === 'timetable' || nextTabId === 'map') {
                  onTabChange(nextTabId);
                }
              }}
              classNames={{
                root: styles.detailsTabs,
                tabBar: styles.detailsTabBar,
                tab: styles.detailsTab,
                activeTab: styles.detailsTabActive,
                content: styles.detailsTabContent,
                panel: styles.detailsTabPanel,
              }}
            >
              <Tab id="timetable" label="Timetable">
                <>
                  {timetableError && (
                    <StateMessage
                      state="error"
                      title="Timetable lookup failed"
                      message={timetableError.message}
                    />
                  )}

                  {unavailableMessage && (
                    <StateMessage
                      state="empty"
                      title="Timetable unavailable"
                      message={unavailableMessage}
                    />
                  )}

                  {!timetableLoading &&
                    timetable &&
                    timetable.journeys.length === 0 && (
                      <StateMessage
                        state="empty"
                        title="No timetable journeys"
                        message="No journey data is available for this timetable."
                      />
                    )}

                  {shouldShowTimetable && (
                    <Timetable
                      aria-label="Line timetable"
                      waypoints={timetable?.waypoints ?? []}
                      journeys={timetable?.journeys ?? []}
                      notices={
                        timetableNotesEnabled ? (timetable?.notices ?? []) : []
                      }
                      assets={lineAssets}
                      direction={selectedDirection}
                      directionLabels={directionLabels}
                      stopDisplay={stopDisplay}
                      controlsStopDisplay={controlsStopDisplay}
                      date={date}
                      loading={timetableLoading}
                      onDirectionChange={onDirectionChange}
                      onStopDisplayChange={onStopDisplayChange}
                      onDateChange={onDateChange}
                      onSubmit={onSubmit}
                      onShowStopDeparturesDetails={onShowStopDeparturesDetails}
                    />
                  )}
                </>
              </Tab>
              <Tab id="map" label="Route Map">
                {(isActive) =>
                  isActive ? (
                    <TimetableMap selectedLine={selectedLine} />
                  ) : (
                    <></>
                  )
                }
              </Tab>
            </Tabs>
          </div>
        </div>
      </ViewportOverlay>
    </DetailsPage>
  );
}
